/**
 * Splitting a rendered block into the things the page draws differently.
 *
 * A block's `text_hi` is Devanagari with two kinds of marker left in it, and the
 * difference between them is the difference between a job and a defect:
 *
 * `⟦key⟧` — **a blank the officer fills.** An unfilled declared slot, or an
 * org-block placeholder the case could not supply. It has an amber chip in the
 * `unresolved` list, it is counted in "N blanks remaining", and clicking it asks
 * for the value. The DOCX renderer draws it as a ruled line.
 *
 * `{{key}}` — **a marker no slot table declares** (open item 26). The renderer
 * draws a rule for it too, so it prints as somewhere to write — but no chip
 * exists, so nobody is ever asked to fill it. That is a real gap and it closes
 * with P10's slot editor. Until then this page marks it as what it is rather
 * than letting it pass for an ordinary blank, because a blank nobody is asked
 * for is the one that reaches a served document empty.
 *
 * The two forms are the server's, in `docx_render._BLANK_RE`. They are matched
 * here rather than sent pre-split because the text is also what a rewrite
 * selects against, and a span offset into a re-assembled string would not be the
 * span the server stored.
 */

export type SegmentKind = 'text' | 'blank' | 'undeclared'

export interface Segment {
  kind: SegmentKind
  /** For `text`, the Devanagari. For the others, the slot key. */
  value: string
  /** Character offset in the block's `text_hi`. What a rewrite span is measured in. */
  start: number
  end: number
}

const MARKER = /⟦([^⟧]*)⟧|\{\{([^}]*)\}\}/g

export function segment(text: string): Segment[] {
  const parts: Segment[] = []
  let cursor = 0
  MARKER.lastIndex = 0

  for (let match = MARKER.exec(text); match !== null; match = MARKER.exec(text)) {
    if (match.index > cursor) {
      parts.push({
        kind: 'text',
        value: text.slice(cursor, match.index),
        start: cursor,
        end: match.index,
      })
    }
    const declared = match[1] !== undefined
    parts.push({
      kind: declared ? 'blank' : 'undeclared',
      value: (declared ? match[1] : match[2]) ?? '',
      start: match.index,
      end: match.index + match[0].length,
    })
    cursor = match.index + match[0].length
  }

  if (cursor < text.length) {
    parts.push({ kind: 'text', value: text.slice(cursor), start: cursor, end: text.length })
  }
  return parts
}

/** Every slot key actually drawn on this page, in order of first appearance. */
export function blanksOn(blocks: { text_hi?: string | null }[]): string[] {
  const seen = new Set<string>()
  const keys: string[] = []
  for (const block of blocks) {
    for (const part of segment(block.text_hi ?? '')) {
      if (part.kind === 'blank' && !seen.has(part.value)) {
        seen.add(part.value)
        keys.push(part.value)
      }
    }
  }
  return keys
}

/**
 * How the blanks counter reads.
 *
 * Counts down to "All blanks filled", which is a statement worth making and
 * therefore worth being exactly true. The count is the server's
 * `blanks_remaining` — pagination is the renderer's and an officer cannot supply
 * a page number, so those blanks are drawn and not counted.
 */
export function readBlankCount(remaining: number): string {
  if (remaining <= 0) return 'All blanks filled'
  return remaining === 1 ? '1 blank remaining' : `${remaining} blanks remaining`
}

/**
 * When a human changed something, for the authorship stack.
 *
 * **Both halves come from the same clock**, which sounds too obvious to write
 * down and was not: the first version took the date by slicing the ISO string,
 * which is UTC, and the time from the local `Date` — so an edit made at 00:29 in
 * Kolkata was stamped `10 Aug 2026 00:29` on the 11th. Wrong by a day for
 * anything written after 18:30 IST, on the one line whose job is to say when a
 * human touched a document.
 */
export function readStamp(
  iso: string | null | undefined,
  format: (day: string) => string,
): string {
  if (!iso) return 'an unrecorded time'
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return 'an unrecorded time'
  const time = `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`
  return `${format(_day(at))} ${time}`
}

/** The calendar day an instant fell on, on the clock the reader is standing on. */
function _day(at: Date): string {
  return `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(
    at.getDate(),
  ).padStart(2, '0')}`
}

/**
 * The day an instant fell on — for the surfaces that show a date and no time.
 *
 * **Never `iso.slice(0, 10)`.** That is the first ten characters of a UTC
 * timestamp, which between 00:00 and 05:30 IST is yesterday: a document version
 * written at 02:47 on the 12th listed itself as the 11th in the Versions panel,
 * and the template and process screens dated their own sign-offs the same way.
 * Slicing a timestamp is day arithmetic on a UTC instant, which is ADR-018's
 * defect wearing a different hat.
 *
 * `readStamp` above was fixed for this once and the raw slice survived on five
 * other surfaces. Both now take the day from `_day`, so a date shown with a time
 * and a date shown alone cannot disagree about which day something happened.
 */
export function readDay(iso: string | null | undefined): string {
  if (!iso) return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  return _day(at)
}

/**
 * What to call a blank whose slot table printed no label for it.
 *
 * `case_recital` carried `—` in D01's label column, so it was the one blank on
 * the page with no name: its hover read only the generic reason and its
 * accessible name was the raw key. Every other blank names itself. A blank the
 * officer cannot name is not an honest blank — it is a gap that says nothing
 * about what belongs in it, which is the whole defect item 34 was about.
 *
 * The skeleton is fixed too; this is the floor under it, so a slot declared
 * tomorrow without a label still reads as words rather than as snake_case.
 */
export function readSlotName(slot: string, labelHi?: string | null): string {
  if (labelHi && labelHi.trim()) return labelHi.trim()
  const words = slot.replace(/\[\]$/, '').replace(/[_.]+/g, ' ').trim()
  return words || slot
}

/**
 * The badge a block's provenance earns, or null where it earns none.
 *
 * `[O]` and `[C]` are plain on purpose. Marking every block would make the two
 * that matter — our unsourced draft, and statutory text awaiting sign-off —
 * invisible among the noise, and stop list 8 is specifically that `[D]` must be
 * *visibly* flagged.
 */
export function provenanceBadge(
  provenance: string | null | undefined,
): { label: string; title: string; tone: 'locked' | 'pending' | 'unsourced' } | null {
  if (provenance === 'S') {
    return {
      label: 'Statutory · locked',
      title:
        'Reproduced verbatim from the rules and carries its citation. No rewrite, no model, ever — a paraphrased statute is a different statute.',
      tone: 'locked',
    }
  }
  if (provenance === 'S/O') {
    return {
      label: 'Awaiting sign-off',
      title:
        'Statutory in appearance, pending the Template administrator’s confirmation. Treated as office text until then.',
      tone: 'pending',
    }
  }
  if (provenance === 'D') {
    return {
      label: 'Unsourced draft',
      title:
        'Drafted by us. No AWEIL sample and no prescribed form behind it — it must be cleared before real use.',
      tone: 'unsourced',
    }
  }
  return null
}

/**
 * The same badge, as an officer reading a draft meets it.
 *
 * `provenanceBadge` speaks the template editor's vocabulary, where an
 * administrator picks *Unsourced draft* from a list of layers and has to see
 * the same two words on the block. On a draft the officer never chose a layer
 * and the words name nothing they know — so the flag stop list 8 requires was
 * on the page and said nothing. Here it says what the paragraph is and what is
 * owed before it is used. It is no quieter: same tone, same place, every time.
 */
export function draftBadge(
  provenance: string | null | undefined,
): ReturnType<typeof provenanceBadge> {
  const badge = provenanceBadge(provenance)
  if (badge?.tone !== 'unsourced') return badge
  return {
    ...badge,
    label: 'Our wording — not yet approved by the office',
    title:
      'We wrote this paragraph. The office has given no sample of it and no form prescribes it, so read it closely and have the wording approved before the document is used.',
  }
}

/* --------------------------------------------------------- names, in words -- */

/** `1st`, `2nd`, `3rd`, `11th`, `22nd`. */
export function readOrdinal(n: number): string {
  const tens = n % 100
  if (tens >= 11 && tens <= 13) return `${n}th`
  const suffix = ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'
  return `${n}${suffix}`
}

/**
 * Which one of several this is — and nothing at all when there is only one.
 *
 * Most documents are written once on a case, and *occurrence 1* beside every
 * one of them is a word from the data model telling the officer nothing. Where
 * the same document has been written more than once — a second reminder, a
 * third noting — which one is open is the first thing to know, and it is said
 * the way the rail says it: *on this case*.
 *
 * `count` is the rail's own number of them. It is used only when it can be
 * true of this one; a stale count smaller than the occurrence is dropped
 * rather than printed as *3rd of 2*.
 */
export function readOccurrence(occurrence: number, count?: number | null): string | null {
  const known = count != null && count >= occurrence ? count : null
  if (occurrence <= 1 && (known ?? 1) <= 1) return null
  return known
    ? `the ${readOrdinal(occurrence)} of ${known} on this case`
    : `the ${readOrdinal(occurrence)} on this case`
}

/* ------------------------------------------------------ the papers beneath -- */

export interface SourceInput {
  file_id: string
  filename?: string | null
  page?: number | null
}

export interface PaperSource {
  file_id: string
  filename: string
  /** Every page of this paper the paragraph rests on, once each, in order. */
  pages: number[]
}

/**
 * Each paper once, with its pages together.
 *
 * A paragraph resting on ten pages of one report listed that report's file name
 * ten times, each with one page — a line an officer reads as ten papers. The
 * papers keep the order they were first cited in; the pages are put in order,
 * because that is how the scan is turned.
 */
export function groupSources(sources: readonly SourceInput[]): PaperSource[] {
  const papers = new Map<string, PaperSource>()
  for (const source of sources) {
    const id = String(source.file_id)
    const paper = papers.get(id) ?? { file_id: id, filename: source.filename ?? '', pages: [] }
    if (!paper.filename && source.filename) paper.filename = source.filename
    if (source.page && !paper.pages.includes(source.page)) paper.pages.push(source.page)
    papers.set(id, paper)
  }
  return [...papers.values()].map((paper) => ({
    ...paper,
    pages: [...paper.pages].sort((a, b) => a - b),
  }))
}

/* ----------------------------------------------------- the checks, in words -- */

/**
 * A catalogue row as a sentence.
 *
 * The checks are written in the skeletons for whoever maintains them, in
 * markdown: `past_penalties` in backticks, a `**BLOCK**` in asterisks. Printed
 * as stored, an officer reads punctuation that means nothing and a field name
 * with an underscore in it. The marks are dropped and a name in backticks is
 * given back its spaces; **no word is added and none is removed**, because a
 * rule this screen paraphrased would be a second wording of a rule the server
 * owns.
 */
export function plainWords(text: string | null | undefined): string {
  return (text ?? '')
    .replace(/`([^`]*)`/g, (_whole, name: string) => name.replace(/_/g, ' '))
    .replace(/[`*]/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .trim()
}

export interface CheckInput {
  rule?: string | null
  n?: number | null
  text: string
  passed: boolean
  evaluated: boolean
}

/** One row's identity. `rule` is absent on some catalogue rows, so it is not enough alone. */
export function checkKey(check: CheckInput): string {
  return `${check.rule ?? ''}|${check.n ?? ''}|${check.text}`
}

export interface SortedChecks<T> {
  /** **The server's blocking set, as sent** — what stops this document going out. */
  stops: T[]
  /** Decided, not passed, and not stopping this document. */
  notPassed: T[]
  /** Nothing decided them. Never reported as passed. */
  undecided: T[]
  passed: T[]
}

/**
 * Every check in exactly one place, around the server's own blocking set.
 *
 * The panel used to label each row by the catalogue's severity while its header
 * counted the server's blocking set, so a row read `BLOCK` under a header
 * reading *0 blocking* — both true, and together a contradiction on the one
 * panel that says whether a document may go out. What stops the document is
 * whatever the server put in `blocking` and nothing else (`web/CLAUDE.md`: the
 * client never decides what is possible); every other row is sorted by what was
 * decided about it, so the counts above the rows are counts of those rows.
 */
export function sortChecks<T extends CheckInput>(
  checks: readonly T[],
  blocking: readonly T[],
): SortedChecks<T> {
  const stopping = new Set(blocking.map(checkKey))
  const rest = checks.filter((check) => !stopping.has(checkKey(check)))
  return {
    stops: [...blocking],
    notPassed: rest.filter((check) => check.evaluated && !check.passed),
    undecided: rest.filter((check) => !check.evaluated),
    passed: rest.filter((check) => check.evaluated && check.passed),
  }
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

/** What stops the document, said first. The count is of the rows listed under it. */
export function readStops(stops: number): string {
  if (stops <= 0) return 'Nothing stops this document going out.'
  return `${stops} ${plural(stops, 'thing stops', 'things stop')} this document going out.`
}

/** Everything else, in one line — only the parts there are. Null when there is nothing else. */
export function readOtherChecks(sorted: {
  notPassed: unknown[]
  undecided: unknown[]
  passed: unknown[]
}): string | null {
  const parts = [
    sorted.notPassed.length ? `${sorted.notPassed.length} did not pass` : '',
    sorted.undecided.length ? readUndecided(sorted.undecided.length) : '',
    sorted.passed.length ? `${sorted.passed.length} passed` : '',
  ].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

/** A check nothing decided is said to be undecided — never counted as passed. */
export function readUndecided(n: number): string {
  return `${n} ${plural(n, 'check', 'checks')} could not be decided from the record`
}
